/**
 * Map a (MentionKind, id) cross-app jump target to a router path.
 *
 * For task and shopping_item, the parent (list) id is unknown at
 * call time. Those return ``/tasks?focus=<id>`` / ``/shopping?focus=<id>``;
 * the destination screen consumes the ``?focus`` query on first
 * paint and redirects (replace) to the canonical deep URL.
 *
 * Event mentions use a ``starts_at``-derived date when available;
 * callers without the date pass ``undefined`` and the helper falls
 * back to ``/calendar`` (the screen lands on today and the user sees
 * the event id is unresolved).
 */

import type { MentionKind } from "../lib/MentionContext";

export interface MentionToOpts {
  /** ISO ``YYYY-MM-DD`` when ``kind === "event"`` and the date is known. */
  eventDate?: string;
  /** Canonical occurrence identity (UTC ISO) when ``kind === "event"``. */
  occ?: string;
}

export function mentionTo(
  kind: MentionKind,
  id: string,
  opts: MentionToOpts = {},
): string {
  switch (kind) {
    case "note":
      return `/notes/${id}`;
    case "task":
      return `/tasks?focus=${encodeURIComponent(id)}`;
    case "event": {
      if (opts.eventDate === undefined) return `/calendar`;
      const base = `/calendar/${opts.eventDate}/${id}`;
      return opts.occ !== undefined ? `${base}?occ=${encodeURIComponent(opts.occ)}` : base;
    }
    case "habit":
      return `/habits/${id}`;
    case "shopping_list":
      return `/shopping/${id}`;
    case "shopping_item":
      return `/shopping?focus=${encodeURIComponent(id)}`;
    case "recipe":
      return `/recipes/${id}`;
  }
}
